import { useBiCommonDataContext } from "../../../../contexts/BiCommonDataContext.ts";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import type { SxProps, Theme } from "@mui/material";
import { Button, Grid } from "@mui/material";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DimensionSelection from "../../../viewer/common/DimensionSelection.tsx";
import { selectDimensionsStructureForCurrentReportType } from "../../../../store/selectors/metaDataSelectors.ts";
import { createCurrentReportAction } from "../../../../store/utils/storeUtils";
import { getDimensionsAllowedInConditions } from "../../../../utilities/conditionDataTypes.ts";
import { createEmptyConditionItem } from "../utilities/createEmptyConditionItem.ts";
import type { ConditionField, DimensionDescriptor } from "../../../../../../../../api/types/svTypes.ts";

interface Props {
  text: string;
  conditions: ConditionField[] | undefined;
  dimensions: DimensionDescriptor[];
  onAddField: (field: ConditionField) => void;
  sx?: SxProps<Theme>;
}

export default function AddCondition({ text, conditions, dimensions, onAddField, sx }: Props) {
  const dispatch = useDispatch();
  const { reportType } = useBiCommonDataContext();
  const dimensionsStructure = useSelector(selectDimensionsStructureForCurrentReportType);

  const [addConditionAnchorEl, setAddConditionAnchorEl] = useState<HTMLElement | null>(null);
  const [showAddConditions, setShowAddConditions] = useState(false);

  const conditionSelection = useMemo(() => {
    const allowedDimensions = getDimensionsAllowedInConditions(dimensions, reportType);
    if (conditions === undefined) return allowedDimensions;
    return allowedDimensions.filter((f) => !conditions.some((fs) => f.name === fs.meta.name));
  }, [conditions, dimensions, reportType]);

  return (
    <Grid sx={sx} ref={setAddConditionAnchorEl}>
      <Button
        sx={{
          px: "5px",
          py: "4px",
          minWidth: 0,
          minHeight: "30px",
          lineHeight: "22px",
          letterSpacing: 0,
          fontSize: "13px",
          fontWeight: 500,
          textWrap: "nowrap",
          justifyContent: "flex-start",
        }}
        startIcon={<AddRoundedIcon sx={{ fontSize: "18px" }} />}
        onClick={() => setShowAddConditions(true)}
      >
        {text}
      </Button>
      {addConditionAnchorEl && showAddConditions && (
        <DimensionSelection
          anchorEl={addConditionAnchorEl}
          dimensions={conditionSelection}
          onClose={(value) => {
            if (value !== undefined) {
              const field = createEmptyConditionItem(value);
              onAddField(field);
              dispatch(createCurrentReportAction("setAutoShowItemOptionsId", field.config.guid));
            }
            setShowAddConditions(false);
          }}
          menuWidth={addConditionAnchorEl.offsetWidth}
          dimensionsStructure={dimensionsStructure}
        />
      )}
    </Grid>
  );
}
